import React from 'react';
import { AccessCheckError } from '@/components/AccessCheckError';
import { useNavigate } from 'react-router-dom';
import { useFeature } from '@/hooks/useFeature';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Lock, TrendingUp, Zap } from '@/components/icons';

interface FeatureGateProps {
  feature: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  showUpgrade?: boolean;
  upgradeMessage?: string;
  /** Override the upgrade action. Defaults to navigating to billing settings. */
  onUpgrade?: () => void;
}

/**
 * Component that conditionally renders children based on feature access
 */
export function FeatureGate({
  feature,
  children,
  fallback,
  showUpgrade = true,
  upgradeMessage,
  onUpgrade,
}: FeatureGateProps) {
  const { hasFeature, isLoading, error, refetch } = useFeature(feature);
  const navigate = useNavigate();
  const handleUpgrade = onUpgrade ?? (() => navigate('/settings?tab=billing'));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) return <AccessCheckError retry={() => refetch?.()} />;

  if (hasFeature) {
    return <>{children}</>;
  }

  if (fallback) {
    return <>{fallback}</>;
  }

  if (showUpgrade) {
    return (
      <Card className="border-dashed border-2">
        <CardHeader className="text-center">
          <CardTitle className="flex items-center justify-center gap-2 text-muted-foreground">
            <Lock className="w-5 h-5" />
            Feature Locked
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-3">
          <p className="text-sm text-muted-foreground">
            {upgradeMessage || `This feature requires a subscription upgrade.`}
          </p>
          <Button variant="outline" size="sm" onClick={handleUpgrade}>
            <TrendingUp className="w-4 h-4 mr-2" />
            Upgrade Plan
          </Button>
        </CardContent>
      </Card>
    );
  }

  return null;
}

interface UsageLimitProps {
  feature: string;
  children: React.ReactNode;
  warningThreshold?: number; // Show warning when usage is above this percentage (0-1)
  blockThreshold?: number; // Block access when usage is above this percentage (0-1)
}

/**
 * Component that shows usage information and potentially blocks access based on limits
 */
export function UsageLimit({
  feature,
  children,
  warningThreshold = 0.8,
  blockThreshold = 1.0
}: UsageLimitProps) {
  const navigate = useNavigate();
  const {
    hasFeature,
    featureLimit,
    currentUsage,
    remainingUsage,
    canUseFeature,
    isLoading, error, refetch
  } = useFeature(feature);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) return <AccessCheckError retry={() => refetch?.()} />;

  if (!hasFeature) {
    return (
      <FeatureGate feature={feature} showUpgrade={true}>
        {children}
      </FeatureGate>
    );
  }

  // If feature has unlimited usage (featureLimit is very high or null)
  if (featureLimit >= 999999) {
    return <>{children}</>;
  }

  const usagePercentage = featureLimit > 0 ? currentUsage / featureLimit : 0;
  const isAtWarningThreshold = usagePercentage >= warningThreshold;
  const isAtBlockThreshold = usagePercentage >= blockThreshold;

  // Block access if over limit
  if (isAtBlockThreshold || !canUseFeature) {
    return (
      <Card className="border-destructive border-2">
        <CardHeader>
          <CardTitle className="flex items-center justify-center gap-2 text-destructive">
            <Zap className="w-5 h-5" />
            Usage Limit Reached
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-3">
          <div className="space-y-2">
            <p className="text-sm font-medium">
              {feature === 'user_seats'
                ? `Active members and pending invitations use all ${featureLimit} seats.`
                : `You've used ${currentUsage} of ${featureLimit} allowed this month.`}
            </p>
            <div className="w-full bg-muted rounded-full h-2">
              <div 
                className="bg-destructive h-2 rounded-full" 
                style={{ width: '100%' }}
              ></div>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            {feature === 'user_seats'
              ? 'Cancel a pending invitation, deactivate a team member, or upgrade your plan.'
              : 'Upgrade your plan or wait until next month to continue using this feature.'}
          </p>
          <Button variant="outline" size="sm" onClick={() => navigate('/settings?tab=billing')}>
            <TrendingUp className="w-4 h-4 mr-2" />
            Upgrade Plan
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Show warning if approaching limit
  if (isAtWarningThreshold) {
    return (
      <div className="space-y-4">
        <Card className="border-warning">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm font-medium">Usage Warning</p>
                <p className="text-xs text-muted-foreground">
                  {remainingUsage} of {featureLimit} remaining this month
                </p>
              </div>
              <Badge variant="secondary">
                {Math.round((1 - usagePercentage) * 100)}% left
              </Badge>
            </div>
            <div className="mt-2 w-full bg-muted rounded-full h-2">
              <div 
                className="bg-warning h-2 rounded-full transition-all duration-300" 
                style={{ width: `${Math.min(100, usagePercentage * 100)}%` }}
              ></div>
            </div>
          </CardContent>
        </Card>
        {children}
      </div>
    );
  }

  return <>{children}</>;
}

interface FeatureToggleProps {
  feature: string;
  children: (enabled: boolean, loading: boolean) => React.ReactNode;
}

/**
 * Render prop component that provides feature status to children
 */
export function FeatureToggle({ feature, children }: FeatureToggleProps) {
  const { hasFeature, isLoading } = useFeature(feature);
  return <>{children(hasFeature, isLoading)}</>;
}